import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MessagesSquare } from "lucide-react";

import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";

/**
 * Back-reference strip for the document viewer: "Used in N chats", listing the chats started
 * from this document that the viewer is allowed to see (the server filters by access). Hidden
 * when no such chats exist, so it never adds noise to documents nothing references.
 */
export function DocumentChatsBackref({ documentId, onClose }: { documentId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: chats } = useQuery({
    queryKey: qk.documentChats(documentId),
    queryFn: () => api.chat.documentChats(documentId),
  });

  if (!chats?.length) return null;

  const openChat = (projectId: string | null | undefined, chatId: string) => {
    onClose();
    if (projectId) {
      void navigate({ to: "/projects/$id/assistant/chat/$chatId", params: { id: projectId, chatId } });
    } else {
      void navigate({ to: "/assistant/chat/$id", params: { id: chatId } });
    }
  };

  return (
    <div className="flex shrink-0 items-center gap-3 overflow-x-auto border-b bg-muted/30 px-4 py-2 text-xs">
      <span className="flex shrink-0 items-center gap-1.5 font-medium text-muted-foreground">
        <MessagesSquare className="size-3.5" aria-hidden />
        Used in {chats.length} {chats.length === 1 ? "chat" : "chats"}
      </span>
      {chats.map((chat) => (
        <button
          key={chat.id}
          type="button"
          onClick={() => openChat(chat.project_id, chat.id)}
          className="shrink-0 rounded-full border bg-card px-2.5 py-1 hover:bg-accent hover:text-accent-foreground"
        >
          {chat.title || "Untitled chat"}
        </button>
      ))}
    </div>
  );
}
